import GeckoStore from 'services/gecko-store';
import GeckoRequest from 'services/gecko-request';
import GeckoSocket from 'services/gecko-socket';
import {
  setLoggedInUser,
  setIsSupport,
  setDefaultHeaders,
  setAPIURL,
  setDateFormat,
  setProfiles,
  setFeatures,
} from 'actions/appState';
import GeckoAuth from '../gecko-auth';

const REGION_API_URLS = {
  stage: 'https://api-eustage.geckochat.io',
  'eu-west-2': 'https://api-euwest2.geckochat.io',
  'us-east-1': 'https://api-useast1.geckochat.io',
  local: process.env.REACT_APP_API_URL,
};
const REGION_DATE_FORMATS = {
  stage: 'DD/MM/YYYY',
  'eu-west-2': 'DD/MM/YYYY',
  'us-east-1': 'MM/DD/YYYY',
  local: 'DD/MM/YYYY',
};

const getAPIUrl = (decodedToken) => {
  const { region } = decodedToken;
  const apiUrl = REGION_API_URLS[region];
  if (!apiUrl) {
    throw new Error(`No API URL for region "${region}"`);
  }
  return apiUrl;
};

const setUserProfiles = (decodedToken) => {
  const { profiles } = decodedToken;
  return GeckoStore.redux.dispatch(setProfiles(profiles));
};

const setDateFormatAndFeatures = (decodedToken) => {
  const { region } = decodedToken;
  const getDateFormat = (account) => {
    if (account.date_format) {
      return account.date_format;
    }
    const dateFormat = REGION_DATE_FORMATS[region];
    if (!dateFormat) {
      throw new Error(`No date format for region "${region}"`);
    }
    return dateFormat;
  };
  const getFeatures = (account) => {
    return account.features || {};
  };
  return GeckoRequest.get('/account').then((account) => {
    GeckoStore.redux.dispatch(setDateFormat(getDateFormat(account)));
    GeckoStore.redux.dispatch(setFeatures(getFeatures(account)));
  });
};

const setUser = () => {
  return GeckoRequest.get('/user/me').then((loggedInUser) => {
    GeckoStore.redux.dispatch(setLoggedInUser(loggedInUser));
  });
};

const setIsSupportUser = () => {
  // If we are already switched into an account we aren't a support user so resolve early
  if (GeckoAuth.hasBackup('support-original-tokens')) {
    return Promise.resolve(false);
  }
  return GeckoRequest.get('/admin/is-support')
    .then(({ isSupport }) => {
      GeckoStore.redux.dispatch(setIsSupport(isSupport));
    })
    .catch((err) => {
      console.error(err);
      return GeckoStore.redux.dispatch(setIsSupport(false));
    });
};

const registerServiceWorker = () => {
  require('services/service-worker');
};

const setupSockets = () => {
  GeckoSocket.init();
};

const saturate = () => {
  // Saturate the models
  return GeckoRequest.get('/saturate-cutdown').then((initialState) => {
    Object.keys(initialState).forEach((key) => {
      const model = key.slice(0, -1).toUpperCase();
      GeckoStore.redux.dispatch({
        type: `NEW_${model}S`,
        payload: initialState[key],
      });
    });
  });
};

let bootstrapListeners = [];
let cleanupFunctions = [];

const runCallback = (callback) => {
  const res = callback();
  if (res instanceof Function) {
    cleanupFunctions.push(res);
  }
};

const runCallbacks = () => {
  bootstrapListeners.forEach(runCallback);
};

export const onBootstrap = (callback) => {
  bootstrapListeners.push(callback);
  if (bootStrapped) {
    runCallback(callback);
  }
};

let bootStrapped = null;

export const bootstrap = (token) => {
  // If we have already or are already bootstrapping just return that promise
  if (bootStrapped) {
    return bootStrapped;
  }

  // Decode the token
  return GeckoAuth.getDecodedToken().then((decodedToken) => {
    // Set the default request headers
    GeckoStore.redux.dispatch(setDefaultHeaders({ Authorization: token }));

    // Set the API url to use
    const apiUrl = getAPIUrl(decodedToken);
    GeckoStore.redux.dispatch(setAPIURL(apiUrl));

    bootStrapped = Promise.resolve()
      .then(setDateFormatAndFeatures.bind(null, decodedToken))
      .then(setUser)
      .then(setUserProfiles.bind(null, decodedToken))
      .then(setIsSupportUser)
      .then(registerServiceWorker)
      .then(setupSockets)
      .then(saturate)
      .then(runCallbacks);
    return bootStrapped;
  });
};

export const clearBootstrap = () => {
  bootStrapped = null;
  cleanupFunctions.forEach((func) => func());
  cleanupFunctions = [];
};

export default {
  bootstrap,
  onBootstrap,
  clearBootstrap,
};
